import { CheckCircle2, ShieldCheck, Radar, Activity } from "lucide-react";
import ServiceSectionShell from "./ServiceSectionShell";

const assessmentDomains = [
  "Web security",
  "Mobile (Android & iOS)",
  "Cloud security",
  "Protocol security",
  "Product security",
  "Network security",
  "Wireless security",
  "Red teaming",
  "Forensic audit (DFIR)",
];

const defensePillars = [
  {
    icon: ShieldCheck,
    title: "Assessment & remediation",
    detail:
      "NIST-aligned security assessments with practical remediation strategies and operator-led execution to close gaps.",
  },
  {
    icon: Radar,
    title: "Cyber defense & SOC",
    detail:
      "Real-time monitoring, threat detection, and incident response delivered by healthcare-fluent security engineers.",
  },
  {
    icon: Activity,
    title: "IoMT protection",
    detail:
      "Medical device discovery, risk scoring, and East-West traffic visibility across clinical networks.",
  },
];

const pulseCapabilities = [
  "Real-time IoT discovery & inventory",
  "Device risk scoring",
  "Automated cost & lifecycle analysis",
  "Device behavior analytics",
  "Tagging: inventory, risk, EOS/EOL",
  "IoT threat detection & response",
  "Geolocation & East-West traffic",
  "Incident response & investigation",
  "Security event management",
  "Remediation tracking & compliance",
];

export default function CyberSecurity() {
  return (
    <ServiceSectionShell
      kicker="Cyber Security Solutions"
      title="Security built for HIPAA, IoMT, and live clinical environments"
      lead="Healthcare is the most targeted sector in cyber. We bring assessments, 24×7 monitoring, and IoMT protection aligned to NIST and HIPAA — built for operators who can't afford a multi-day outage."
      tone="dark"
    >
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {defensePillars.map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.title}
              className="group rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-burnt-sienna-400/40 hover:bg-white/[0.06] sm:p-6"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-complement-500/15 text-burnt-sienna-300 ring-1 ring-complement-500/30">
                <Icon className="h-4 w-4" strokeWidth={2.2} />
              </span>
              <h4 className="mt-4 text-[1.02rem] font-bold text-white">
                {p.title}
              </h4>
              <p className="mt-2 text-[0.88rem] leading-relaxed text-white/60">
                {p.detail}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1.4fr] lg:gap-8">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm sm:p-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-burnt-sienna-400">
            Assessment services
          </p>
          <h4 className="mt-2 text-[1.02rem] font-bold text-white">
            Nine domains, one program
          </h4>
          <p className="mt-2 text-[0.86rem] leading-relaxed text-white/55">
            Comprehensive evaluation across the full healthcare attack surface.
          </p>
          <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {assessmentDomains.map((d) => (
              <li
                key={d}
                className="flex items-start text-[0.85rem] text-white/75"
              >
                <CheckCircle2 className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-complement-400" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-sm sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-burnt-sienna-400">
                IoMT defense
              </p>
              <h4 className="mt-2 text-[1.02rem] font-bold text-white">
                Culinda Pulse &mdash; healthcare IoT monitoring
              </h4>
            </div>
            <span className="shrink-0 rounded-full border border-white/15 bg-white/[0.06] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white/60">
              Partnership
            </span>
          </div>
          <p className="mt-2 text-[0.86rem] leading-relaxed text-white/55">
            Real-time, agentless monitoring of every medical device on your
            network — with risk scoring, behavior analytics, and incident
            response.
          </p>
          <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {pulseCapabilities.map((p) => (
              <li
                key={p}
                className="flex items-start text-[0.84rem] text-white/75"
              >
                <CheckCircle2 className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-complement-400" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ServiceSectionShell>
  );
}
